import React, { createContext, useContext } from 'react';
import useEnhancedPremiumStatus from '../hooks/useEnhancedPremiumStatus';
import {
  FREE_COOKBOOK_CREATIONS_PER_MONTH,
  FREE_MANUAL_SNIPPET_CREATIONS_PER_MONTH,
} from '../constants/subscriptionTier';

// Create the context
export const PremiumContext = createContext();

// Create a provider component
export const PremiumProvider = ({ children }) => {
  // Use the enhanced premium status hook that handles all verification methods
  const {
    isPremium,
    hasPaidSubscription,
    planType,
    partnerProUntil,
    partnerCode,
    partnerName,
    subscriptionData,
    isLoading,
    refreshPremiumStatus,
  } = useEnhancedPremiumStatus();

  // Value to be provided by the context
  const value = {
    isPremium,
    hasPaidSubscription,
    planType,
    partnerProUntil,
    partnerCode,
    partnerName,
    subscriptionData,
    isLoading,
    refreshPremiumStatus,
    // Premium feature access controls
    features: {
      aiExplanations: {
        allowed: isPremium,
        unlimited: isPremium,
        dailyLimit: isPremium ? Infinity : 0,
      },
      spacedRepetition: {
        advanced: isPremium,
      },
      themes: {
        custom: isPremium,
      },
      ads: {
        disabled: isPremium,
      },
      localStorage: {
        enabled: isPremium,
      },
      dividers: {
        customIcons: isPremium,
        customColors: isPremium,
      },
      snippets: {
        monthlyManualCreationsLimit: isPremium ? null : FREE_MANUAL_SNIPPET_CREATIONS_PER_MONTH,
        unlimited: isPremium,
      },
      cookbooks: {
        monthlyCreationsLimit: isPremium ? null : FREE_COOKBOOK_CREATIONS_PER_MONTH,
        unlimited: isPremium,
      },
      roadmap: {
        fullCatalogAccess: isPremium,
      },
    }
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};

// Custom hook to use the premium context
export const usePremium = () => {
  const context = useContext(PremiumContext);
  if (context === undefined) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
};
